import { useState } from 'react'
import { useSupabaseQuery } from '../../hooks/useSupabase'

export default function ProviderConfigForm({ config, onSubmit, onCancel, loading }) {
  const { data: providers } = useSupabaseQuery('WhatsAppProviders', {
    order: { column: 'WhatsAppProviderName', ascending: true }
  })

  const [formData, setFormData] = useState({
    Name: config?.Name || '',
    WhatsAppProviderId: config?.WhatsAppProviderId || '',
    JsonConfig: config?.JsonConfig ? JSON.stringify(config.JsonConfig, null, 2) : '',
  })
  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}
    if (!formData.Name.trim()) newErrors.Name = 'Name is required'
    if (!formData.WhatsAppProviderId) newErrors.WhatsAppProviderId = 'Provider is required'

    if (formData.JsonConfig.trim()) {
      try {
        JSON.parse(formData.JsonConfig)
      } catch (e) {
        newErrors.JsonConfig = 'Must be valid JSON'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    const submitData = {
      Name: formData.Name.trim(),
      WhatsAppProviderId: Number(formData.WhatsAppProviderId),
      JsonConfig: formData.JsonConfig.trim() ? JSON.parse(formData.JsonConfig) : {},
    }

    const result = await onSubmit(submitData)
    if (result?.error) {
      setErrors({ submit: result.error.message })
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Config Name *
        </label>
        <input
          type="text"
          value={formData.Name}
          onChange={(e) => handleChange('Name', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Production Meta Config"
        />
        {errors.Name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Provider *
        </label>
        <select
          value={formData.WhatsAppProviderId}
          onChange={(e) => handleChange('WhatsAppProviderId', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">Select a provider...</option>
          {providers?.map((p) => (
            <option key={p.WhatsAppProviderId} value={p.WhatsAppProviderId}>
              {p.WhatsAppProviderName}
            </option>
          ))}
        </select>
        {errors.WhatsAppProviderId && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.WhatsAppProviderId}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Configuration (JSON)
        </label>
        <textarea
          value={formData.JsonConfig}
          onChange={(e) => handleChange('JsonConfig', e.target.value)}
          rows={8}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
          placeholder={'{\n  "AccessToken": "your-access-token",\n  "PhoneNumberId": "your-phone-number-id",\n  "GraphBase": "https://graph.facebook.com/v21.0"\n}'}
        />
        {errors.JsonConfig && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.JsonConfig}</p>}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Meta WhatsApp Business API configuration (AccessToken, PhoneNumberId, GraphBase)
        </p>
      </div>

      {errors.submit && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{errors.submit}</p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition disabled:opacity-50 flex items-center space-x-2"
        >
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{config ? 'Update' : 'Add'} Provider Config</span>
        </button>
      </div>
    </form>
  )
}
